/**
 * Cash Position report — Opening / Receipts / Payments / Bank Deposits /
 * Closing per branch per day. Mirrors the bookkeeper's till format:
 *
 *   Opening + Receipts - Payments = Closing       (Payments INCLUDES Bank Deposits)
 *   Bank Deposits row is highlighted separately so accountants can see
 *   "cash that moved to bank" vs "cash actually spent".
 *
 * Backed by GET /api/reports/cash-position.
 */
import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";

export default function CashPositionReport({ dateFrom, dateTo, branchId, branchTypeId }) {
  const [data, setData] = useState({ branches: [], days: [] });
  const [loading, setLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (branchId) params.branch_id = branchId;
        if (branchTypeId) params.branch_type_id = branchTypeId;
        const { data } = await api.get("/reports/cash-position", { params });
        setData(data);
        // If the top-bar branch filter is set, sync internal selector.
        if (branchId) {
          setSelectedBranch(branchId);
        } else if (data.branches?.length && !selectedBranch) {
          setSelectedBranch(data.branches[0].branch_id);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, branchId, branchTypeId]);

  const branch = useMemo(
    () => data.branches.find((b) => b.branch_id === selectedBranch) || data.branches[0],
    [data, selectedBranch],
  );

  if (loading) {
    return <div className="bg-canvas-card border border-canvas-line p-10 text-center text-xs text-ink-subtle">Loading…</div>;
  }

  if (!branch) {
    return <div className="bg-canvas-card border border-canvas-line p-10 text-center text-xs text-ink-subtle">No branches in scope.</div>;
  }

  return (
    <div className="space-y-4" data-testid="cash-position-report">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Branch:</span>
        <select
          data-testid="cash-position-branch"
          value={selectedBranch}
          onChange={(e) => setSelectedBranch(e.target.value)}
          className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        >
          {data.branches.map((b) => (
            <option key={b.branch_id} value={b.branch_id}>
              {b.branch_name} — closing ₹{b.current_closing.toLocaleString("en-IN")}
            </option>
          ))}
        </select>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
          Seed: {fmtMoney(branch.opening_cash)} {branch.opening_cash_as_of ? `· as of ${branch.opening_cash_as_of}` : "(set by admin in Branch settings)"}
        </span>
      </div>

      <div className="border-2 border-ink overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-canvas-panel">
            <tr>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-left whitespace-nowrap">Date</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-right whitespace-nowrap">Opening</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-right whitespace-nowrap">+ Receipts</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-right whitespace-nowrap text-ink-subtle"  >&nbsp;&nbsp;sales</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-right whitespace-nowrap text-ink-subtle">&nbsp;&nbsp;bookings</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-right whitespace-nowrap text-ink-subtle">&nbsp;&nbsp;service</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-right whitespace-nowrap text-ink-subtle">&nbsp;&nbsp;vouchers</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-right whitespace-nowrap">− Payments</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-right whitespace-nowrap text-ink-subtle">&nbsp;&nbsp;expenses</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-right whitespace-nowrap text-[#0EA5E9]">&nbsp;&nbsp;bank deposit</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-right whitespace-nowrap">Closing</th>
            </tr>
          </thead>
          <tbody>
            {branch.days.length === 0 && (
              <tr>
                <td colSpan={11} className="px-3 py-10 text-center text-ink-muted">
                  No cash movements in this period.
                </td>
              </tr>
            )}
            {branch.days.map((d) => (
              <tr key={d.date} className="border-t border-canvas-line">
                <td className="px-3 py-1.5 font-mono whitespace-nowrap">{d.date}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap">{fmtMoney(d.opening)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-[#00713a]">{fmtMoney(d.receipts)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-ink-subtle">{fmtMoney(d.sales_in)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-ink-subtle">{fmtMoney(d.booking_in)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-ink-subtle">{fmtMoney(d.service_in)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-ink-subtle">{fmtMoney(d.voucher_in)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-destructive">{fmtMoney(d.payments)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-ink-subtle">{fmtMoney(d.voucher_out)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-[#0EA5E9]">{fmtMoney(d.bank_deposits)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums whitespace-nowrap font-bold">{fmtMoney(d.closing)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-ink-subtle">
        Cash drawer ledger — cash-method tenders only. <span className="text-[#0EA5E9]">Bank deposits</span> are
        included in Payments (they leave the till) but listed separately so they're not confused with expenses.
        Opening = previous day's Closing (Day-0 opening = the branch's admin-set seed in Branch settings).
      </p>

      <div className="border-2 border-ink p-4 bg-canvas-panel/30">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted mb-2">Multi-Branch Summary</div>
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider">Branch</th>
              <th className="text-right px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider">Current Closing</th>
            </tr>
          </thead>
          <tbody>
            {data.branches.map((b) => (
              <tr key={b.branch_id} className="border-t border-canvas-line">
                <td className="px-3 py-1.5">{b.branch_name}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums font-bold">{fmtMoney(b.current_closing)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-ink bg-canvas-panel/60">
              <td className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider">All Branches</td>
              <td className="px-3 py-1.5 text-right font-mono tabular-nums font-bold">
                {fmtMoney(data.branches.reduce((s, b) => s + (b.current_closing || 0), 0))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
